import { useState, useCallback, useEffect, useRef } from 'react';
import { saveComponentByType } from '../utils/buildComponentSavePayload';

/**
 * Auto-save hook for inline canvas editing.
 * Debounces saves on field changes and fires a final save on unmount (collapse/deselect).
 *
 * Usage in a template:
 *   const { triggerSave, saveStatus } = useAutoSave(componentId, componentType);
 *   // Call triggerSave() in every field's onChange handler
 *
 * @param {string} componentId
 * @param {string} componentType - e.g. 'email', 'code', 'calculator'
 * @param {object} [options]
 * @param {number} [options.delay=800] - debounce delay in ms
 * @param {boolean} [options.enabled=true] - set false to disable (e.g. when feature flag is off)
 * @returns {{ triggerSave: () => void, saveStatus: 'idle'|'saving'|'saved'|'error', lastSavedAt: number|null }}
 */
export default function useAutoSave(componentId, componentType, options = {}) {
  const { delay = 800, enabled = true } = options;

  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const dirtyRef = useRef(false); // tracks whether user actually changed something

  // Keep refs to latest values so the unmount cleanup uses current data
  const componentIdRef = useRef(componentId);
  const componentTypeRef = useRef(componentType);
  componentIdRef.current = componentId;
  componentTypeRef.current = componentType;

  const doSave = useCallback(async () => {
    if (!mountedRef.current) return;
    setSaveStatus('saving');
    try {
      const res = await saveComponentByType(componentTypeRef.current, componentIdRef.current);
      if (mountedRef.current) {
        if (res && res._blocked) {
          // storeWriteGuard blocked the save — show error, not a false "saved"
          setSaveStatus('error');
        } else {
          setSaveStatus('saved');
          setLastSavedAt(Date.now());
        }
      }
    } catch (e) {
      console.error('[useAutoSave] save failed:', componentTypeRef.current, componentIdRef.current, e);
      if (mountedRef.current) {
        setSaveStatus('error');
      }
    }
  }, []);

  const triggerSave = useCallback(() => {
    if (!enabled) return;
    dirtyRef.current = true;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(doSave, delay);
  }, [enabled, delay, doSave]);

  // Save on unmount (collapse / deselect) — fire-and-forget, but ONLY if user made changes
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
      if (enabled && dirtyRef.current) {
        saveComponentByType(componentTypeRef.current, componentIdRef.current).catch(() => {});
      }
    };
  }, [enabled]);

  // Clear timer on componentId change
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, [componentId]);

  return { triggerSave, saveStatus, lastSavedAt };
}
